import React from 'react';
import { CityPageProps } from 'screens/AreaPage/types';
import { ScrollSpyProvider, ScrollSpyTarget } from 'components/scroll-spy';
import {
  WithContentSlotBottomPadding,
  WithContentSlotSidePaddingContainer,
  WithContentSlotTopPadding,
} from 'ds/components/Layout';
import DetailsSection from 'components/details-section';
import { OverviewSection } from 'screens/AreaPage/sections/Overview';
import Metrics from 'screens/AreaPage/sections/Metrics';
import { Section } from 'components/navigation/subheader/SectionsNavigation';
import { StatsSection } from 'screens/AreaPage/sections/Stats';
import { SchoolsSection } from 'screens/AreaPage/sections/Schools';
import { SocialRankingSection } from 'screens/AreaPage/sections/SocialRanking';
import ListingsSection from 'components/listings-section';
import NearbyProjectsSection from 'components/nearby-projects-section';
import Footer from 'components/footer';
import Trends from 'components/widget/trends';
import { H3 } from 'ds/components/typography';
import { PricesBorder } from 'components/widget/prices/section/styled';
import Listings from 'components/widget/prices/section/listings';
import { useLocale } from 'locale';
import { SectionHeader, SocialRankingSectionWrapper, LocalWinnersSection, TrendsChartTitle, BlogSectionBg, BlogWrap } from 'screens/AreaPage/styled';
import { LocalWinners } from 'components/madad/local-winners/LocalWinners';
import { isArrayEmpty } from 'utils/arrayUtils';
import ProjectAdBanner from 'components/ads/ProjectAdBanner';
import ProjectAdBannerSource from 'components/ads/ProjectAdBannerSource';
import { AdGetter } from 'components/ads/types';
import BlogSection from 'components/blog-carousel/area-page';
import LeadingAgentsCarousel from 'components/leading-agent-carousel';


const CityPage: React.FC<CityPageProps> = props => {

  const {
    localData,
    setVisibleSection,
    searchArea,
    pricesItemsCount,
    madadAgencies,
    articles,
    schoolsData,
    leadingAgents,
  } = props;

  const { t } = useLocale();

  if (!localData || !searchArea) return null;

  const showLeadingAgents = leadingAgents && leadingAgents.length;
  const {
    description,
    kindergartenInArea,
    demographic,
    userRatings,
    city,
  } = localData;

  // The demographic index is incorrect (old data) in "localDate.zoneSummary", so we use it directly. Can be removed after the BE is fixed (ILDEV-9756)
  const summary = demographic ? {
    ...localData.zoneSummary,
    demographicIndex: demographic.demographicIndex as number,
  } : localData.zoneSummary;

  return (
    <ScrollSpyProvider onChange={setVisibleSection}>
      <ProjectAdBannerSource>
        {(getBanner: AdGetter) => (
          <>
            <WithContentSlotBottomPadding data-auto="madlan-city-page">
              <DetailsSection zoneName={searchArea.name} type={searchArea.type} location={searchArea.location} />
              <WithContentSlotTopPadding style={{ paddingTop: 0 }}>
                <WithContentSlotSidePaddingContainer>

                  <ScrollSpyTarget targetId={Section.OVERVIEW_AREA}>
                    <OverviewSection description={description} areaCityName={city} />
                  </ScrollSpyTarget>

                  <Metrics summary={summary} type={searchArea.type} />

                  <ProjectAdBanner fullWidth ad={getBanner()} mTop={7} mBottom={7} />
                  <Trends
                    outerTitle={(
                      <ScrollSpyTarget targetId={Section.TRENDS}>
                        <SectionHeader>
                          <H3 weight="bold">{t('localPage.prices.title2', { area: searchArea.name })}</H3>
                        </SectionHeader>
                      </ScrollSpyTarget>
                    )}
                    chartTitle={(
                      <ScrollSpyTarget targetId={Section.PRICE_TRENDS}>
                        <TrendsChartTitle />
                      </ScrollSpyTarget>
                    )}
                  />

                  <ScrollSpyTarget targetId={Section.DEALS}>
                    <SectionHeader>
                      <H3 weight="bold">{t('widget.prices.tableAreaHeader', {
                        count: pricesItemsCount,
                        area: searchArea.name,
                      })}</H3>
                    </SectionHeader>
                    <PricesBorder>
                      <Listings showTabs={false} />
                    </PricesBorder>
                  </ScrollSpyTarget>

                  {!isArrayEmpty(madadAgencies) ? (
                    <ScrollSpyTarget targetId={Section.MADAD}>
                      <LocalWinnersSection>
                        <LocalWinners
                          agents={[]}
                          agencies={madadAgencies}
                          area="city"
                        />
                      </LocalWinnersSection>
                    </ScrollSpyTarget>
                  ) : null}

                  <ProjectAdBanner fullWidth ad={getBanner()} mTop={7} mBottom={7} />

                  <ScrollSpyTarget targetId={Section.REVIEWS}>
                    <StatsSection userRatings={userRatings} />
                  </ScrollSpyTarget>

                  {showLeadingAgents ? (
                    <WithContentSlotTopPadding>
                      <LeadingAgentsCarousel items={leadingAgents} source="city-page" />
                    </WithContentSlotTopPadding>
                  ) : null}

                  <ScrollSpyTarget targetId={Section.SCHOOLS}>
                    <SchoolsSection
                      schoolData={schoolsData}
                      kindergartenInArea={kindergartenInArea}
                      areaCityName={city}
                    />
                  </ScrollSpyTarget>

                  <ScrollSpyTarget targetId={Section.SOCIAL_INDEX}>
                    <SocialRankingSectionWrapper>
                      <SocialRankingSection demographic={demographic} areaCityName={city} />
                    </SocialRankingSectionWrapper>
                  </ScrollSpyTarget>
                </WithContentSlotSidePaddingContainer>

                {articles && articles.length ? (
                  <BlogSectionBg>
                    <BlogWrap>
                      <BlogSection />
                    </BlogWrap>
                  </BlogSectionBg>
                ) : null}

                <WithContentSlotSidePaddingContainer>
                  <ScrollSpyTarget targetId={Section.LISTINGS}>
                    <ListingsSection
                      term={searchArea.docId}
                      zoneName={searchArea.name}
                      type={searchArea.type}
                      areaCityName={city}
                    />
                  </ScrollSpyTarget>

                  <ScrollSpyTarget targetId={Section.PROJECTS}>
                    <NearbyProjectsSection
                      term={searchArea.docId}
                      zoneName={searchArea.name}
                      type={searchArea.type}
                      areaCityName={city}
                    />
                  </ScrollSpyTarget>

                </WithContentSlotSidePaddingContainer>
              </WithContentSlotTopPadding>
            </WithContentSlotBottomPadding>
            <ScrollSpyTarget targetId={Section.FOOTER}>
              <Footer />
            </ScrollSpyTarget>
          </>
        )}
      </ProjectAdBannerSource>
    </ScrollSpyProvider>
  );
};

export default CityPage;
